import { MapContext } from '@react-google-maps/api';
import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const defaultOptions = {};

function MarkerFunctional({
  animation,
  children,
  clickable,
  clusterer,
  cursor,
  draggable,
  icon,
  id,
  label,
  noClustererRedraw,
  onAnimationChanged,
  onClick,
  onClickableChanged,
  onCursorChanged,
  onDblClick,
  onDrag,
  onDragEnd,
  onDragStart,
  onDraggableChanged,
  onFlatChanged,
  onIconChanged,
  onLoad,
  onMouseDown,
  onMouseOut,
  onMouseOver,
  onMouseUp,
  onPositionChanged,
  onRightClick,
  onShapeChanged,
  onTitleChanged,
  onUnmount,
  onVisibleChanged,
  onZindexChanged,
  opacity,
  options,
  position,
  shape,
  title,
  visible,
  zIndex
}) {
  const map = useContext(MapContext);

  const [instance, setInstance] = useState(null);

  const [dblclickListener, setDblclickListener] = useState(null);
  const [dragendListener, setDragendListener] = useState(null);
  const [dragstartListener, setDragstartListener] = useState(null);
  const [mousedownListener, setMousedownListener] = useState(null);
  const [mouseoutListener, setMouseoutListener] = useState(null);
  const [mouseoverListener, setMouseoverListener] = useState(null);
  const [mouseupListener, setMouseupListener] = useState(null);
  const [rightclickListener, setRightclickListener] = useState(null);
  const [clickListener, setClickListener] = useState(null);
  const [dragListener, setDragListener] = useState(null);

  const [clickableChangedListener, setClickableChangedListener] = useState(null);
  const [cursorChangedListener, setCursorChangedListener] = useState(null);
  const [animationChangedListener, setAnimationChangedListener] = useState(null);
  const [draggableChangedListener, setDraggableChangedListener] = useState(null);
  const [flatChangedListener, setFlatChangedListener] = useState(null);
  const [iconChangedListener, setIconChangedListener] = useState(null);
  const [positionChangedListener, setPositionChangedListener] = useState(null);
  const [shapeChangedListener, setShapeChangedListener] = useState(null);
  const [titleChangedListener, setTitleChangedListener] = useState(null);
  const [visibleChangedListener, setVisibleChangedListener] = useState(null);
  const [zIndexChangedListener, setZindexChangedListener] = useState(null);

  // Order does matter
  useEffect(() => {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);

  useEffect(() => {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);

  useEffect(() => {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);

  useEffect(() => {
    if (position && instance !== null) {
      instance.setPosition(position);
    }
  }, [instance, position]);

  useEffect(() => {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);

  useEffect(() => {
    if (animation && instance !== null) {
      instance.setAnimation(animation);
    }
  }, [instance, animation]);

  useEffect(() => {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        window.google.maps.event.removeListener(dblclickListener);
      }

      setDblclickListener(
        window.google.maps.event.addListener(instance, 'dblclick', onDblClick)
      );
    }
  }, [onDblClick]);

  useEffect(() => {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        window.google.maps.event.removeListener(dragendListener);
      }

      setDragendListener(
        window.google.maps.event.addListener(instance, 'dragend', onDragEnd)
      );
    }
  }, [onDragEnd]);

  useEffect(() => {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        window.google.maps.event.removeListener(dragstartListener);
      }

      setDragstartListener(
        window.google.maps.event.addListener(instance, 'dragstart', onDragStart)
      );
    }
  }, [onDragStart]);

  useEffect(() => {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        window.google.maps.event.removeListener(mousedownListener);
      }

      setMousedownListener(
        window.google.maps.event.addListener(instance, 'mousedown', onMouseDown)
      );
    }
  }, [onMouseDown]);

  useEffect(() => {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        window.google.maps.event.removeListener(mouseoutListener);
      }

      setMouseoutListener(
        window.google.maps.event.addListener(instance, 'mouseout', onMouseOut)
      );
    }
  }, [onMouseOut]);

  useEffect(() => {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        window.google.maps.event.removeListener(mouseoverListener);
      }

      setMouseoverListener(
        window.google.maps.event.addListener(instance, 'mouseover', onMouseOver)
      );
    }
  }, [onMouseOver]);

  useEffect(() => {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        window.google.maps.event.removeListener(mouseupListener);
      }

      setMouseupListener(
        window.google.maps.event.addListener(instance, 'mouseup', onMouseUp)
      );
    }
  }, [onMouseUp]);

  useEffect(() => {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        window.google.maps.event.removeListener(rightclickListener);
      }

      setRightclickListener(
        window.google.maps.event.addListener(instance, 'rightclick', onRightClick)
      );
    }
  }, [onRightClick]);

  useEffect(() => {
    if (instance && onClick) {
      if (clickListener !== null) {
        window.google.maps.event.removeListener(clickListener);
      }

      setClickListener(
        window.google.maps.event.addListener(instance, 'click', onClick)
      );
    }
  }, [onClick]);

  useEffect(() => {
    if (instance && onDrag) {
      if (dragListener !== null) {
        window.google.maps.event.removeListener(dragListener);
      }

      setDragListener(
        window.google.maps.event.addListener(instance, 'drag', onDrag)
      );
    }
  }, [onDrag]);

  useEffect(() => {
    if (instance && onClickableChanged) {
      if (clickableChangedListener !== null) {
        window.google.maps.event.removeListener(clickableChangedListener);
      }

      setClickableChangedListener(
        window.google.maps.event.addListener(instance, 'clickable_changed', onClickableChanged)
      );
    }
  }, [onClickableChanged]);

  useEffect(() => {
    if (instance && onCursorChanged) {
      if (cursorChangedListener !== null) {
        window.google.maps.event.removeListener(cursorChangedListener);
      }

      setCursorChangedListener(
        window.google.maps.event.addListener(instance, 'cursor_changed', onCursorChanged)
      );
    }
  }, [onCursorChanged]);

  useEffect(() => {
    if (instance && onAnimationChanged) {
      if (animationChangedListener !== null) {
        window.google.maps.event.removeListener(animationChangedListener);
      }

      setAnimationChangedListener(
        window.google.maps.event.addListener(instance, 'animation_changed', onAnimationChanged)
      );
    }
  }, [onAnimationChanged]);

  useEffect(() => {
    if (instance && onDraggableChanged) {
      if (draggableChangedListener !== null) {
        window.google.maps.event.removeListener(draggableChangedListener);
      }

      setDraggableChangedListener(
        window.google.maps.event.addListener(instance, 'draggable_changed', onDraggableChanged)
      );
    }
  }, [onDraggableChanged]);

  useEffect(() => {
    if (instance && onFlatChanged) {
      if (flatChangedListener !== null) {
        window.google.maps.event.removeListener(flatChangedListener);
      }

      setFlatChangedListener(
        window.google.maps.event.addListener(instance, 'flat_changed', onFlatChanged)
      );
    }
  }, [onFlatChanged]);

  useEffect(() => {
    if (instance && onIconChanged) {
      if (iconChangedListener !== null) {
        window.google.maps.event.removeListener(iconChangedListener);
      }

      setIconChangedListener(
        window.google.maps.event.addListener(instance, 'icon_changed', onIconChanged)
      );
    }
  }, [onIconChanged]);

  useEffect(() => {
    if (instance && onPositionChanged) {
      if (positionChangedListener !== null) {
        window.google.maps.event.removeListener(positionChangedListener);
      }

      setPositionChangedListener(
        window.google.maps.event.addListener(instance, 'position_changed', onPositionChanged)
      );
    }
  }, [onPositionChanged]);

  useEffect(() => {
    if (instance && onShapeChanged) {
      if (shapeChangedListener !== null) {
        window.google.maps.event.removeListener(shapeChangedListener);
      }

      setShapeChangedListener(
        window.google.maps.event.addListener(instance, 'shape_changed', onShapeChanged)
      );
    }
  }, [onShapeChanged]);

  useEffect(() => {
    if (instance && onTitleChanged) {
      if (titleChangedListener !== null) {
        window.google.maps.event.removeListener(titleChangedListener);
      }

      setTitleChangedListener(
        window.google.maps.event.addListener(instance, 'title_changed', onTitleChanged)
      );
    }
  }, [onTitleChanged]);

  useEffect(() => {
    if (instance && onVisibleChanged) {
      if (visibleChangedListener !== null) {
        window.google.maps.event.removeListener(visibleChangedListener);
      }

      setVisibleChangedListener(
        window.google.maps.event.addListener(instance, 'visible_changed', onVisibleChanged)
      );
    }
  }, [onVisibleChanged]);

  useEffect(() => {
    if (instance && onZindexChanged) {
      if (zIndexChangedListener !== null) {
        window.google.maps.event.removeListener(zIndexChangedListener);
      }

      setZindexChangedListener(
        window.google.maps.event.addListener(instance, 'zindex_changed', onZindexChanged)
      );
    }
  }, [onZindexChanged]);

  useEffect(() => {
    const markerOptions = {
      ...(options || defaultOptions),
      ...(clusterer ? defaultOptions : { map }),
      position
    };

    const marker = new window.google.maps.Marker(markerOptions);

    marker.id = id;

    if (clusterer) {
      clusterer.addMarker(marker, !!noClustererRedraw);
    } else {
      marker.setMap(map);
    }

    if (position) {
      marker.setPosition(position);
    }

    if (typeof visible !== 'undefined') {
      marker.setVisible(visible);
    }

    if (typeof draggable !== 'undefined') {
      marker.setDraggable(draggable);
    }

    if (typeof clickable !== 'undefined') {
      marker.setClickable(clickable);
    }

    if (typeof cursor === 'string') {
      marker.setCursor(cursor);
    }

    if (icon) {
      marker.setIcon(icon);
    }

    if (typeof label !== 'undefined') {
      marker.setLabel(label);
    }

    if (typeof opacity !== 'undefined') {
      marker.setOpacity(opacity);
    }

    if (shape) {
      marker.setShape(shape);
    }

    if (typeof title === 'string') {
      marker.setTitle(title);
    }

    if (typeof zIndex === 'number') {
      marker.setZIndex(zIndex);
    }

    if (onDblClick) {
      setDblclickListener(
        window.google.maps.event.addListener(marker, 'dblclick', onDblClick)
      );
    }

    if (onDragEnd) {
      setDragendListener(
        window.google.maps.event.addListener(marker, 'dragend', onDragEnd)
      );
    }

    if (onDragStart) {
      setDragstartListener(
        window.google.maps.event.addListener(marker, 'dragstart', onDragStart)
      );
    }

    if (onMouseDown) {
      setMousedownListener(
        window.google.maps.event.addListener(marker, 'mousedown', onMouseDown)
      );
    }

    if (onMouseOut) {
      setMouseoutListener(
        window.google.maps.event.addListener(marker, 'mouseout', onMouseOut)
      );
    }

    if (onMouseOver) {
      setMouseoverListener(
        window.google.maps.event.addListener(marker, 'mouseover', onMouseOver)
      );
    }

    if (onMouseUp) {
      setMouseupListener(
        window.google.maps.event.addListener(marker, 'mouseup', onMouseUp)
      );
    }

    if (onRightClick) {
      setRightclickListener(
        window.google.maps.event.addListener(marker, 'rightclick', onRightClick)
      );
    }

    if (onClick) {
      setClickListener(
        window.google.maps.event.addListener(marker, 'click', onClick)
      );
    }

    if (onDrag) {
      setDragListener(
        window.google.maps.event.addListener(marker, 'drag', onDrag)
      );
    }

    if (onClickableChanged) {
      setClickableChangedListener(
        window.google.maps.event.addListener(marker, 'clickable_changed', onClickableChanged)
      );
    }

    if (onCursorChanged) {
      setCursorChangedListener(
        window.google.maps.event.addListener(marker, 'cursor_changed', onCursorChanged)
      );
    }

    if (onAnimationChanged) {
      setAnimationChangedListener(
        window.google.maps.event.addListener(marker, 'animation_changed', onAnimationChanged)
      );
    }

    if (onDraggableChanged) {
      setDraggableChangedListener(
        window.google.maps.event.addListener(marker, 'draggable_changed', onDraggableChanged)
      );
    }

    if (onFlatChanged) {
      setFlatChangedListener(
        window.google.maps.event.addListener(marker, 'flat_changed', onFlatChanged)
      );
    }

    if (onIconChanged) {
      setIconChangedListener(
        window.google.maps.event.addListener(marker, 'icon_changed', onIconChanged)
      );
    }

    if (onPositionChanged) {
      setPositionChangedListener(
        window.google.maps.event.addListener(marker, 'position_changed', onPositionChanged)
      );
    }

    if (onShapeChanged) {
      setShapeChangedListener(
        window.google.maps.event.addListener(marker, 'shape_changed', onShapeChanged)
      );
    }

    if (onTitleChanged) {
      setTitleChangedListener(
        window.google.maps.event.addListener(marker, 'title_changed', onTitleChanged)
      );
    }

    if (onVisibleChanged) {
      setVisibleChangedListener(
        window.google.maps.event.addListener(marker, 'visible_changed', onVisibleChanged)
      );
    }

    if (onZindexChanged) {
      setZindexChangedListener(
        window.google.maps.event.addListener(marker, 'zindex_changed', onZindexChanged)
      );
    }

    setInstance(marker);

    if (onLoad) {
      onLoad(marker);
    }

    return () => {
      if (dblclickListener !== null) {
        window.google.maps.event.removeListener(dblclickListener);
      }

      if (dragendListener !== null) {
        window.google.maps.event.removeListener(dragendListener);
      }

      if (dragstartListener !== null) {
        window.google.maps.event.removeListener(dragstartListener);
      }

      if (mousedownListener !== null) {
        window.google.maps.event.removeListener(mousedownListener);
      }

      if (mouseoutListener !== null) {
        window.google.maps.event.removeListener(mouseoutListener);
      }

      if (mouseoverListener !== null) {
        window.google.maps.event.removeListener(mouseoverListener);
      }

      if (mouseupListener !== null) {
        window.google.maps.event.removeListener(mouseupListener);
      }

      if (rightclickListener !== null) {
        window.google.maps.event.removeListener(rightclickListener);
      }

      if (clickListener !== null) {
        window.google.maps.event.removeListener(clickListener);
      }

      if (clickableChangedListener !== null) {
        window.google.maps.event.removeListener(clickableChangedListener);
      }

      if (cursorChangedListener !== null) {
        window.google.maps.event.removeListener(cursorChangedListener);
      }

      if (animationChangedListener !== null) {
        window.google.maps.event.removeListener(animationChangedListener);
      }

      if (draggableChangedListener !== null) {
        window.google.maps.event.removeListener(draggableChangedListener);
      }

      if (flatChangedListener !== null) {
        window.google.maps.event.removeListener(flatChangedListener);
      }

      if (iconChangedListener !== null) {
        window.google.maps.event.removeListener(iconChangedListener);
      }

      if (positionChangedListener !== null) {
        window.google.maps.event.removeListener(positionChangedListener);
      }

      if (titleChangedListener !== null) {
        window.google.maps.event.removeListener(titleChangedListener);
      }

      if (visibleChangedListener !== null) {
        window.google.maps.event.removeListener(visibleChangedListener);
      }

      if (zIndexChangedListener !== null) {
        window.google.maps.event.removeListener(zIndexChangedListener);
      }

      if (onUnmount) {
        onUnmount(marker);
      }

      if (clusterer) {
        clusterer.removeMarker(marker, !!noClustererRedraw);
      } else if (marker) {
        marker.setMap(null);
      }
    };
  }, []);

  const chx = useMemo(() => {
    return children
      ? Children.map(children, child => {
        if (!isValidElement(child)) {
          return child;
        }

        const elementChild = child;

        return cloneElement(elementChild, { anchor: instance });
      })
      : null;
  }, [children, instance]);

  return <>{chx}</> || null;
}

export const MarkerF = memo(MarkerFunctional);

export default MarkerF;
